import { useState, useEffect } from "react";

import {
  createPrintFileInvoice,
  getEndCustomerByEndCustomerID,
  updateInvoice,
} from "API/swesum";

import {
  Box,
  Dialog,
  Typography,
  Button,
  Grid,
  TextField,
  Switch,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import InfoField from "Components/Invoice/InfoField";
import { usePopupNotificationContext } from "Context/NotificationPopup";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: 4,
  width: "100&",
  height: "100%",
};

const infoFieldFontSize = 13;

export default function PrintModal({
  invoice,
  setInvoice,
  fetchPrintedInvoices,
  open,
  onClose,
}) {
  const [ourReference, setOurReference] = useState(invoice?.OurReference);
  const [externalReference, setExternalReference] = useState(
    invoice?.ExternalReference
  );
  const [orderNumber, setOrderNumber] = useState(invoice?.OrderNo);
  const [gln, setGln] = useState(invoice?.GLN);
  const [endCustomerData, setEndCustomerData] = useState(null);
  const [useEndCustomer, setUseEndCustomer] = useState(false);
  const [loading, setLoading] = useState(false);

  const { openSuccess, openError } = usePopupNotificationContext();

  const handlePrint = async () => {
    setLoading(true);
    try {
      if (
        ourReference !== invoice?.OurReference ||
        externalReference !== invoice?.ExternalReference ||
        orderNumber !== invoice?.OrderNo ||
        gln !== invoice?.GLN
      ) {
        const updatedInvoice = {
          ...invoice,
          OurReference: ourReference,
          ExternalReference: externalReference,
          OrderNo: orderNumber,
          GLN: gln,
        };
        const res = await updateInvoice(invoice?.Id, updatedInvoice);
        if (res.status === 201) {
          setInvoice(updatedInvoice);
        }
      }
      await createPrintFileInvoice(invoice?.Id, useEndCustomer);
      openSuccess("Utskrivning lyckades");
    } catch (error) {
      openError("Något gick fel under utskrivning");
    }
    fetchPrintedInvoices();
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    const fetchEndcustomer = async () => {
      if (invoice?.EndCustomerId) {
        const res = await getEndCustomerByEndCustomerID(invoice?.EndCustomerId);
        setEndCustomerData(res);
      }
    };

    fetchEndcustomer();
  }, [invoice]);

  useEffect(() => {
    setExternalReference(invoice?.ExternalReference);
    setOurReference(invoice?.OurReference);
    setOrderNumber(invoice?.OrderNo);
    setLoading(false);
  }, [open, invoice]);

  return (
    <>
      <Dialog open={open}>
        <Box sx={style}>
          <Typography variant="h5" fontWeight={"bold"}>
            Vill du skriva ut fakturan igen?
          </Typography>
          <Box height={"100%"} pl={3}>
            <Grid container mb={2} rowGap={1}>
              <Grid item container>
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Externt Kundnummer"}
                  md={4}
                  value={
                    useEndCustomer
                      ? endCustomerData?.ExternalCustomerNr
                      : invoice?.EndCustomerExternalCustomerNo
                  }
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Slutkund"}
                  md={4}
                  value={
                    useEndCustomer
                      ? endCustomerData?.Name
                      : invoice?.EndCustomerName
                  }
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Org. nr"}
                  md={4}
                  value={
                    useEndCustomer
                      ? endCustomerData?.OrgNo
                      : invoice?.EndCustomerOrgNo
                  }
                />
              </Grid>
              <Grid item container>
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Adress"}
                  md={4}
                  value={
                    useEndCustomer
                      ? endCustomerData?.Address
                      : invoice?.EndCustomerAddress
                  }
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Adress 2"}
                  md={4}
                  value={
                    useEndCustomer
                      ? endCustomerData?.EndCustomerAddress2
                      : invoice?.EndCustomerAddress2
                  }
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Post. nr"}
                  md={4}
                  value={
                    useEndCustomer
                      ? endCustomerData?.PostalCode
                      : invoice?.EndCustomerPostalCode
                  }
                />
              </Grid>
              <InfoField
                fontSize={infoFieldFontSize}
                label={"Postort"}
                md={4}
                value={
                  useEndCustomer
                    ? endCustomerData?.City
                    : invoice?.EndCustomerCity
                }
              />
            </Grid>
            <Grid item container rowGap={1}>
              <Grid item container>
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Belopp"}
                  md={4}
                  value={`${invoice?.Price}:-`}
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Fakturadatum"}
                  md={4}
                  value={invoice?.InvoiceDate}
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"FFD Datum"}
                  md={4}
                  value={invoice?.OriginalFfdDate}
                />
              </Grid>
              <Grid item container>
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Utskriven"}
                  md={4}
                  value={invoice?.CreateTime}
                />
                <InfoField
                  fontSize={infoFieldFontSize}
                  label={"Nästa Händelse"}
                  md={4}
                  value={null}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid item container mt={2} gap={3} justifyContent={"space-evenly"}>
            <TextField
              label="Vår Referens"
              variant="standard"
              value={ourReference}
              onChange={(e) => setOurReference(e.target.value)}
            />
            <TextField
              label="Er Referens"
              variant="standard"
              value={externalReference}
              onChange={(e) => setExternalReference(e.target.value)}
            />
            <TextField
              label="Ordernummer"
              variant="standard"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
            />
            <TextField
              label="GLN"
              variant="standard"
              value={gln}
              onChange={(e) => setGln(e.target.value)}
            />
          </Grid>
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            width={"100%"}
          >
            <Typography fontWeight={"medium"}>
              Använd slutkundsuppgifter
            </Typography>
            <Switch
              value={useEndCustomer}
              onChange={(e) => setUseEndCustomer(e.target.checked)}
            />
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={5}
            justifyContent={"space-around"}
            height={55}
            width={"100%"}
          >
            <Button
              sx={{
                backgroundColor: "transparent",
                outline: "1px solid #1976d2",
                color: "#1976d2",
                width: "100%",
              }}
              onClick={onClose}
            >
              Avbryt
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handlePrint}
              loading={loading}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                width: "100%",
              }}
            >
              Skriv ut
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
